/**
 * Página de Progreso - Gestión de Especialidades, Maestrías y Clases Progresivas
 * Diseño visual moderno con galería de insignias
 */

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Input,
  Select,
  SpecialtyImage,
} from "@/components/ui";
import {
  Award,
  Plus,
  Search,
  Loader2,
  Trophy,
  GraduationCap,
  ChevronRight,
  ChevronDown,
  User,
  Calendar,
  X,
  Filter,
  ClipboardCheck,
} from "lucide-react";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import {
  useAchievementsByClub,
  useClassProgressByClub,
} from "@/hooks/useProgress";
import { useMembersByClub } from "@/hooks/usePersons";
import {
  useAcademicPeriods,
  useSpecialties,
  useMaestrias,
  useCertificaciones,
  useProgressClasses,
} from "@/hooks/useCatalogs";
import AchievementModal from "./components/AchievementModal";
import EditAchievementModal from "./components/EditAchievementModal";
import ClassProgressModal from "./components/ClassProgressModal";
import MemberProgressModal from "./components/MemberProgressModal";
import MyProgressCard from "../profile/components/MyProgressCard";
import type {
  Achievement,
  ClassProgress,
  MemberWithPerson as Member,
} from "@/types";

const achievementTypeLabels: Record<string, string> = {
  specialty: "Especialidad",
  mastery: "Maestría",
  certification: "Certificación",
  class: "Clase",
  investiture: "Investidura",
  position: "Cargo",
};

const achievementTypeColors: Record<string, string> = {
  specialty: "bg-purple-100 text-purple-800",
  mastery: "bg-amber-100 text-amber-800",
  certification: "bg-cyan-100 text-cyan-800",
  class: "bg-green-100 text-green-800",
  investiture: "bg-yellow-100 text-yellow-800",
  position: "bg-gray-100 text-gray-800",
};

const classStatusLabels: Record<string, string> = {
  in_progress: "En progreso",
  completed: "Completada",
  pending_approval: "Pendiente de aprobación",
  approved: "Aprobada",
  rejected: "Rechazada",
  invested: "Investido",
};

const classStatusColors: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  pending_approval: "bg-orange-100 text-orange-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  invested: "bg-yellow-100 text-yellow-800",
};

// Mapa de imágenes de pins para clases progresivas de conquistadores
const classPinImages: Record<string, string> = {
  Amigo: "/images/class/amigo.jpg",
  "Amigo Avanzado": "/images/class/amigo.jpg",
  Compañero: "/images/class/companero.jpg",
  "Compañero Avanzado": "/images/class/companero.jpg",
  Explorador: "/images/class/explorador.jpg",
  "Explorador Avanzado": "/images/class/explorador.jpg",
  Orientador: "/images/class/orientador.jpg",
  "Orientador Avanzado": "/images/class/orientador.jpg",
  Viajero: "/images/class/viajero.jpg",
  "Viajero Avanzado": "/images/class/viajero.jpg",
  Guía: "/images/class/guia.jpg",
  "Guía Avanzado": "/images/class/guia.jpg",
};

// Orden de las clases progresivas
const classOrder: Record<string, number> = {
  Amigo: 1,
  "Amigo Avanzado": 2,
  Compañero: 3,
  "Compañero Avanzado": 4,
  Explorador: 5,
  "Explorador Avanzado": 6,
  Orientador: 7,
  "Orientador Avanzado": 8,
  Viajero: 9,
  "Viajero Avanzado": 10,
  Guía: 11,
  "Guía Avanzado": 12,
};

export default function ProgressPage() {
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<
    "achievements" | "classes" | "members"
  >("achievements");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedAchievement, setSelectedAchievement] =
    useState<Achievement | null>(null);
  const [selectedClassProgress, setSelectedClassProgress] =
    useState<ClassProgress | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  // Data - usando scope del usuario
  const { profile } = useAuthStore();
  const {
    isClubDirector,
    isAssociationAdmin,
    isUnionAdmin,
    isAdmin,
    canManage,
  } = usePermissions();
  // Solo quien puede GESTIONAR progreso (director de club / admin con manage) ve
  // los controles de registrar/editar. Los demás roles (miembro, asociación) ven
  // la página en modo lectura.
  const canManageProgress = canManage("progress");
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: periods } = useAcademicPeriods();
  const { data: achievements, isLoading: loadingAchievements } =
    useAchievementsByClub(selectedClubId, selectedPeriodId || undefined);
  const { data: classProgress, isLoading: loadingClasses } =
    useClassProgressByClub(selectedClubId, selectedPeriodId || undefined);
  const { data: members } = useMembersByClub(selectedClubId);
  const { data: specialties } = useSpecialties();
  const { data: maestrias } = useMaestrias();
  const { data: certificaciones } = useCertificaciones();
  const { data: progressClassesCatalog } = useProgressClasses();

  // Estado para expandir/colapsar clases
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(
    new Set(),
  );

  // Mapa de miembros para búsqueda rápida
  const memberMap = useMemo(() => {
    const map = new Map<string, { name: string; photoUrl?: string }>();
    members?.forEach((m) => {
      const name =
        m.person.fullName ||
        `${m.person.firstName || ""} ${m.person.lastName || ""}`.trim();
      map.set(m.id, { name, photoUrl: m.person.photoUrl });
    });
    return map;
  }, [members]);

  // Mapa de especialidades para obtener categorySlug
  const specialtyMap = useMemo(() => {
    const map = new Map<string, { name: string; categorySlug: string }>();
    specialties?.forEach((s) => {
      map.set(s.id, { name: s.name, categorySlug: s.categorySlug || "" });
    });
    return map;
  }, [specialties]);

  // Mapa de maestrías para obtener imagen
  const maestriaMap = useMemo(() => {
    const map = new Map<string, { name: string; localImage: string }>();
    maestrias?.forEach((m) => {
      map.set(m.id, { name: m.name, localImage: m.localImage || "" });
    });
    return map;
  }, [maestrias]);

  // Mapa de certificaciones para obtener imagen
  const certificacionMap = useMemo(() => {
    const map = new Map<string, { name: string; localImage: string }>();
    certificaciones?.forEach((c) => {
      map.set(c.id, { name: c.name, localImage: c.localImage || "" });
    });
    return map;
  }, [certificaciones]);

  // Auto-seleccionar club si es director o primer club disponible
  useMemo(() => {
    if (!selectedClubId && clubs && clubs.length > 0) {
      // Si es director de club, seleccionar su club
      if (isClubDirector && profile?.scopeId) {
        const directorClub = clubs.find((c) => c.id === profile.scopeId);
        if (directorClub) {
          setSelectedClubId(directorClub.id);
          return;
        }
      }
      // Si no, seleccionar el primer club
      setSelectedClubId(clubs[0].id);
    }
  }, [clubs, selectedClubId, isClubDirector, profile]);

  // Filtrar logros por búsqueda
  const filteredAchievements = useMemo(() => {
    if (!achievements) return [];
    if (!searchTerm) return achievements;

    const term = searchTerm.toLowerCase();
    return achievements.filter((a) => {
      const memberInfo = memberMap.get(a.memberId);
      const memberName = memberInfo?.name?.toLowerCase() || "";
      return (
        a.catalogName.toLowerCase().includes(term) ||
        memberName.includes(term) ||
        (a.instructorName && a.instructorName.toLowerCase().includes(term))
      );
    });
  }, [achievements, searchTerm, memberMap]);

  // Filtrar clases por búsqueda
  const filteredClasses = useMemo(() => {
    if (!classProgress) return [];
    if (!searchTerm) return classProgress;

    const term = searchTerm.toLowerCase();
    return classProgress.filter((c) => {
      const memberInfo = memberMap.get(c.memberId);
      const memberName = memberInfo?.name?.toLowerCase() || "";
      return (
        c.className.toLowerCase().includes(term) || memberName.includes(term)
      );
    });
  }, [classProgress, searchTerm, memberMap]);

  // Mapa de clases del catálogo para obtener orden
  const progressClassMap = useMemo(() => {
    const map = new Map<string, { order: number; clubType: string }>();
    progressClassesCatalog?.forEach((c) => {
      map.set(c.name, { order: c.order, clubType: c.clubType });
    });
    return map;
  }, [progressClassesCatalog]);

  // Agrupar clases por nombre de clase y ordenar
  const classesByName = useMemo(() => {
    const grouped: Record<string, ClassProgress[]> = {};
    filteredClasses.forEach((cp) => {
      if (!grouped[cp.className]) {
        grouped[cp.className] = [];
      }
      grouped[cp.className].push(cp);
    });
    return grouped;
  }, [filteredClasses]);

  // Ordenar las clases por su orden del catálogo
  const sortedClassNames = useMemo(() => {
    return Object.keys(classesByName).sort((a, b) => {
      // Primero usar el orden del catálogo si existe
      const orderA = progressClassMap.get(a)?.order ?? classOrder[a] ?? 999;
      const orderB = progressClassMap.get(b)?.order ?? classOrder[b] ?? 999;
      return orderA - orderB;
    });
  }, [classesByName, progressClassMap]);

  const toggleClassExpanded = (className: string) => {
    setExpandedClasses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(className)) {
        newSet.delete(className);
      } else {
        newSet.add(className);
      }
      return newSet;
    });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Verificar si puede aprobar clases (admins de asociación o unión)
  const canApproveClasses = isAssociationAdmin || isUnionAdmin || isAdmin;

  // Un MIEMBRO común (sin gestión ni aprobación) no debe ver la página de
  // gestión de club (selector de club, "Gestiona…", registrar avances). En su
  // lugar ve su propio medallero — sus especialidades, clases y logros.
  const isMemberView = !canManageProgress && !canApproveClasses;

  if (isMemberView) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Award className="w-5 h-5 text-white" />
            </div>
            Mi Progreso
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Tus especialidades, maestrías y clases progresivas
          </p>
        </div>
        <MyProgressCard />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Award className="w-5 h-5 text-white" />
            </div>
            Progreso y Logros
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Gestiona especialidades, maestrías y clases progresivas
          </p>
        </div>
        <div className="flex gap-2">
          {/* Botón de aprobaciones solo para directores de asociación */}
          {canApproveClasses && (
            <Link to="/progress/approvals">
              <Button
                variant="secondary"
                className="border-orange-300 text-orange-700 hover:bg-orange-50 shadow-md transition-colors duration-200"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Aprobar Clases
              </Button>
            </Link>
          )}
          {activeTab === "achievements" &&
            selectedClubId &&
            canManageProgress && (
              <Can permission="progress.write" clubId={selectedClubId}>
                <Button
                  onClick={() => setShowAchievementModal(true)}
                  className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Especialidad
                </Button>
              </Can>
            )}
          {activeTab === "classes" && selectedClubId && canManageProgress && (
            <Can permission="progress.write" clubId={selectedClubId}>
              <Button
                onClick={() => setShowClassModal(true)}
                className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
              >
                <Plus className="w-4 h-4 mr-2" />
                Registrar Avance
              </Button>
            </Can>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ver especialidades del club
              </label>
              <Select
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value)}
                disabled={loadingClubs}
                className="border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-colors duration-200"
              >
                <option value="">Seleccionar club...</option>
                {clubs?.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Período
              </label>
              <Select
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                className="border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-colors duration-200"
              >
                <option value="">Todos los períodos</option>
                {periods?.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-colors duration-200"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs Modernos */}
      <div className="flex gap-2 p-2 bg-white rounded-2xl shadow-md w-fit">
        <button
          onClick={() => setActiveTab("achievements")}
          className={`px-5 py-2.5 font-medium rounded-xl text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "achievements"
              ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/30"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <Award className="w-4 h-4" />
          Especialidades y Maestrías
          {achievements && achievements.length > 0 && (
            <Badge
              className={`text-xs ml-1 ${activeTab === "achievements" ? "bg-white/20 text-white" : ""}`}
            >
              {achievements.length}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab("classes")}
          className={`px-5 py-2.5 font-medium rounded-xl text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "classes"
              ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          Clases Progresivas
          {classProgress && classProgress.length > 0 && (
            <Badge
              className={`text-xs ml-1 ${activeTab === "classes" ? "bg-white/20 text-white" : ""}`}
            >
              {classProgress.length}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab("members")}
          className={`px-5 py-2.5 font-medium rounded-xl text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "members"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <User className="w-4 h-4" />
          Por Miembro
          {members && members.length > 0 && (
            <Badge
              className={`text-xs ml-1 ${activeTab === "members" ? "bg-white/20 text-white" : ""}`}
            >
              {members.filter((m) => m.status === "active").length}
            </Badge>
          )}
        </button>
      </div>

      {/* Content */}
      {!selectedClubId ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Filter className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-gray-500">
              Selecciona un club para ver su progreso
            </p>
          </CardContent>
        </Card>
      ) : activeTab === "achievements" ? (
        /* Achievements Tab */
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                <Award className="w-5 h-5 text-white" />
              </div>
              Especialidades y Maestrías
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAchievements ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredAchievements.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Award className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-gray-600 font-medium mb-4">
                  {searchTerm
                    ? "No se encontraron resultados"
                    : "No hay especialidades ni maestrías registradas"}
                </p>
                {canManageProgress && (
                  <Can permission="progress.write" clubId={selectedClubId}>
                    <Button
                      onClick={() => setShowAchievementModal(true)}
                      className="rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg shadow-purple-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Primera Especialidad
                    </Button>
                  </Can>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Nombre
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Tipo
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Miembro
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Fecha
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Instructor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAchievements.map((achievement) => {
                      const memberInfo = memberMap.get(achievement.memberId);
                      const specialtyInfo = specialtyMap.get(
                        achievement.catalogId,
                      );
                      const maestriaInfo = maestriaMap.get(
                        achievement.catalogId,
                      );
                      const certificacionInfo = certificacionMap.get(
                        achievement.catalogId,
                      );
                      return (
                        <tr
                          key={achievement.id}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                          onClick={() => setSelectedAchievement(achievement)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {achievement.type === "mastery" ? (
                                // Imagen de maestría
                                <img
                                  src={
                                    maestriaInfo?.localImage ||
                                    "/images/specialties/placeholder.png"
                                  }
                                  alt={achievement.catalogName}
                                  className="w-10 h-10 rounded-xl object-contain bg-amber-50"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src =
                                      "/images/specialties/placeholder.png";
                                  }}
                                />
                              ) : achievement.type === "certification" ? (
                                // Imagen de certificación
                                <img
                                  src={
                                    certificacionInfo?.localImage ||
                                    "/images/specialties/placeholder.png"
                                  }
                                  alt={achievement.catalogName}
                                  className="w-10 h-10 rounded-xl object-contain bg-cyan-50"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src =
                                      "/images/specialties/placeholder.png";
                                  }}
                                />
                              ) : achievement.type === "specialty" ? (
                                // Imagen de especialidad
                                <SpecialtyImage
                                  name={achievement.catalogName}
                                  categorySlug={
                                    specialtyInfo?.categorySlug || ""
                                  }
                                  size="sm"
                                />
                              ) : (
                                // Otros tipos (investidura, cargo, etc.)
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-100">
                                  <Award className="w-5 h-5 text-gray-600" />
                                </div>
                              )}
                              <span className="font-medium text-gray-900">
                                {achievement.catalogName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge
                              className={
                                achievementTypeColors[achievement.type]
                              }
                            >
                              {achievementTypeLabels[achievement.type]}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {memberInfo?.photoUrl ? (
                                <img
                                  src={memberInfo.photoUrl}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover shadow-sm"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-sm">
                                  <User className="w-4 h-4 text-white" />
                                </div>
                              )}
                              <span className="text-gray-900">
                                {memberInfo?.name || "Miembro"}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(achievement.earnedDate)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {achievement.instructorName || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "classes" ? (
        /* Classes Tab */
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              Clases Progresivas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingClasses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredClasses.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Trophy className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-gray-600 font-medium mb-4">
                  {searchTerm
                    ? "No se encontraron resultados"
                    : "No hay clases progresivas registradas"}
                </p>
                {canManageProgress && (
                  <Can permission="progress.write" clubId={selectedClubId}>
                    <Button
                      onClick={() => setShowClassModal(true)}
                      className="rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Primera Clase
                    </Button>
                  </Can>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {sortedClassNames.map((className) => {
                  const progresses = classesByName[className];
                  const pinImage = classPinImages[className];
                  return (
                    <div
                      key={className}
                      className="border-2 border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-colors duration-200"
                    >
                      <button
                        onClick={() => toggleClassExpanded(className)}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3">
                          {pinImage ? (
                            <img
                              src={pinImage}
                              alt={className}
                              className="w-12 h-12 rounded-xl object-cover shadow-md"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-md shadow-green-500/25">
                              <Trophy className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div className="text-left">
                            <h3 className="font-medium text-gray-900">
                              {className}
                            </h3>
                            <p className="text-sm font-medium text-gray-500">
                              {progresses.length} miembro
                              {progresses.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex gap-2">
                            {/* Stats */}
                            {progresses.filter((p) => p.status === "invested")
                              .length > 0 && (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                {
                                  progresses.filter(
                                    (p) => p.status === "invested",
                                  ).length
                                }{" "}
                                investidos
                              </Badge>
                            )}
                            {progresses.filter((p) => p.status === "completed")
                              .length > 0 && (
                              <Badge className="bg-green-100 text-green-800">
                                {
                                  progresses.filter(
                                    (p) => p.status === "completed",
                                  ).length
                                }{" "}
                                completados
                              </Badge>
                            )}
                            {progresses.filter(
                              (p) => p.status === "in_progress",
                            ).length > 0 && (
                              <Badge className="bg-blue-100 text-blue-800">
                                {
                                  progresses.filter(
                                    (p) => p.status === "in_progress",
                                  ).length
                                }{" "}
                                en progreso
                              </Badge>
                            )}
                          </div>
                          {expandedClasses.has(className) ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {expandedClasses.has(className) && (
                        <div className="border-t divide-y">
                          {progresses.map((progress) => {
                            const memberInfo = memberMap.get(progress.memberId);
                            return (
                              <div
                                key={progress.id}
                                className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                                onClick={() =>
                                  setSelectedClassProgress(progress)
                                }
                              >
                                <div className="flex items-center gap-3">
                                  {memberInfo?.photoUrl ? (
                                    <img
                                      src={memberInfo.photoUrl}
                                      alt=""
                                      className="w-10 h-10 rounded-full object-cover shadow-sm"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-sm">
                                      <User className="w-5 h-5 text-white" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {memberInfo?.name || "Miembro"}
                                    </p>
                                    <p className="text-sm font-medium text-gray-500">
                                      {progress.completionPercentage}%
                                      completado
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="w-32 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                      style={{
                                        width: `${progress.completionPercentage}%`,
                                      }}
                                    />
                                  </div>
                                  <Badge
                                    className={
                                      classStatusColors[progress.status]
                                    }
                                  >
                                    {classStatusLabels[progress.status]}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "members" ? (
        /* Members Tab */
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                <User className="w-5 h-5 text-white" />
              </div>
              Progreso por Miembro
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!members || members.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <User className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-gray-500">
                  No hay miembros registrados en este club
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {members
                  .filter((m) => m.status === "active")
                  .filter(
                    (m) =>
                      searchTerm === "" ||
                      m.person.fullName
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                  )
                  .map((member) => {
                    const memberAchievements =
                      achievements?.filter((a) => a.memberId === member.id) ||
                      [];
                    const memberClasses =
                      classProgress?.filter((c) => c.memberId === member.id) ||
                      [];
                    const totalProgress =
                      memberAchievements.length + memberClasses.length;

                    return (
                      <div
                        key={member.id}
                        onClick={() => setSelectedMember(member)}
                        className="flex items-center justify-between p-4 border-2 border-gray-100 rounded-xl hover:bg-gray-50 hover:border-gray-200 cursor-pointer transition-all duration-200"
                      >
                        <div className="flex items-center gap-3">
                          {member.person.photoUrl ? (
                            <img
                              src={member.person.photoUrl}
                              alt=""
                              className="w-12 h-12 rounded-xl object-cover shadow-md"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-md">
                              <User className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.person.fullName ||
                                `${member.person.firstName || ""} ${member.person.lastName || ""}`.trim()}
                            </p>
                            <p className="text-sm font-medium text-gray-500">
                              {totalProgress === 0
                                ? "Sin progreso registrado"
                                : `${memberAchievements.length} logros, ${memberClasses.length} clases`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {memberAchievements.length > 0 && (
                            <Badge className="bg-purple-100 text-purple-800">
                              {memberAchievements.length} logros
                            </Badge>
                          )}
                          {memberClasses.length > 0 && (
                            <Badge className="bg-green-100 text-green-800">
                              {memberClasses.length} clases
                            </Badge>
                          )}
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Achievement Modal */}
      {showAchievementModal && (
        <AchievementModal
          isOpen={showAchievementModal}
          clubId={selectedClubId}
          periodId={selectedPeriodId}
          onClose={() => setShowAchievementModal(false)}
        />
      )}

      {/* Class Progress Modal - Crear nuevo */}
      {showClassModal && (
        <ClassProgressModal
          isOpen={showClassModal}
          clubId={selectedClubId}
          periodId={selectedPeriodId}
          onClose={() => setShowClassModal(false)}
        />
      )}

      {/* Class Progress Modal - Ver/editar existente */}
      {selectedClassProgress && (
        <ClassProgressModal
          isOpen={!!selectedClassProgress}
          clubId={selectedClubId}
          periodId={selectedPeriodId}
          classProgress={selectedClassProgress}
          onClose={() => setSelectedClassProgress(null)}
        />
      )}

      {/* Edit Achievement Modal */}
      {selectedAchievement && (
        <EditAchievementModal
          isOpen={!!selectedAchievement}
          achievement={selectedAchievement}
          onClose={() => setSelectedAchievement(null)}
        />
      )}

      {/* Member Progress Modal */}
      {selectedMember && (
        <MemberProgressModal
          isOpen={!!selectedMember}
          member={selectedMember}
          clubId={selectedClubId}
          periodId={selectedPeriodId}
          onClose={() => setSelectedMember(null)}
          onAddAchievement={() => {
            setSelectedMember(null);
            setShowAchievementModal(true);
          }}
          onAddClass={() => {
            setSelectedMember(null);
            setShowClassModal(true);
          }}
        />
      )}
    </div>
  );
}
